import { ProbabilityCategory } from './ProbabilityCategory';

/**
 * Dehydration likelihood gauge for the Electrolytes section.
 *
 * Same shape as the Glucose gauge (GlucoseAnalysis.calculateGlucoseRegulation…):
 * exactly one tier fires, it sets a numeric probability, and the probability
 * maps to a ProbabilityCategory at the standard 90 / 70 / 50 boundaries so the
 * gauge's colour bands and its label always agree.
 *
 * The three dehydration variants already computed on BloodworkAnalysis
 * (calculateDehydrationProbabilty / …Acute… / …Chronic…) are category-only —
 * Highly Likely / Likely / Possible with no number, which is why they could
 * render as a text line but never as a gauge. This module keeps those same
 * marker rules and attaches a % to each, so nothing about *when* dehydration is
 * flagged changes; only the presentation gains a number.
 *
 * Precedence matches deriveDehydrationText(): chronic beats acute beats
 * general, so the most specific pattern the markers support is the one shown.
 *
 * The percentages are the piece that is a clinical judgement rather than a
 * mechanical translation — they are laid out in TIERS below, one line each, so
 * they can be re-tuned without touching any logic. Re-run
 * scripts/pullDehydrationTiers.js after editing to refresh the sheet.
 */

export type DehydrationType = 'chronic' | 'acute' | 'general' | 'none';

export type DehydrationMarker = {
  direction: 'up' | 'down';
  label: string;
};

export type DehydrationGauge = {
  probability: number;
  category: ProbabilityCategory;
  type: DehydrationType;
  /** Wording for the gauge label, e.g. "Chronic Dehydration is". */
  text: string;
  /** Gauge-driver categoryId — the "why this %" row in FurtherConsiderations. */
  driverCategoryId: number;
  markers: DehydrationMarker[];
};

/**
 * The tier table. `key` is <type>:<tier>, matching the branches already in
 * BloodworkAnalysis. Chronic sits above acute at every tier because it carries
 * the extra protein/BUN evidence; the general variant tops out at Likely
 * because it fires with no CMP at all (isCmpOk() requires those markers blank),
 * so hemoconcentration is all it can ever see.
 */
export const TIERS: {
  [key: string]: { probability: number; categoryId: number; label: string };
} = {
  'chronic:Highly Likely': { probability: 95, categoryId: 9200, label: 'Chronic Dehydration Highly Likely (95%)' },
  'chronic:Likely':        { probability: 85, categoryId: 9205, label: 'Chronic Dehydration Likely (85%)' },
  'chronic:Possible':      { probability: 65, categoryId: 9210, label: 'Chronic Dehydration Possible (65%)' },
  'acute:Highly Likely':   { probability: 92, categoryId: 9215, label: 'Acute Dehydration Highly Likely (92%)' },
  'acute:Likely':          { probability: 80, categoryId: 9220, label: 'Acute Dehydration Likely (80%)' },
  'acute:Possible':        { probability: 62, categoryId: 9225, label: 'Acute Dehydration Possible (62%)' },
  'general:Likely':        { probability: 75, categoryId: 9230, label: 'Dehydration Likely (75%)' },
  'general:Possible':      { probability: 60, categoryId: 9235, label: 'Dehydration Possible (60%)' },
};

/**
 * Less Likely ladder, used when no tier fires. Mirrors the glucose gauge's
 * confidence ladder: every measured marker is optimal, so the number reflects
 * how much evidence was actually gathered rather than how bad the result is.
 * Fewer markers measured → a higher residual %, because less was ruled out.
 */
export const LESS_LIKELY_LADDER = [
  { probability: 5,  categoryId: 9240, label: 'Less Likely (5%) — full hydration panel measured and optimal' },
  { probability: 15, categoryId: 9245, label: 'Less Likely (15%) — RBC/HGB/HCT + Sodium measured and optimal' },
  { probability: 25, categoryId: 9250, label: 'Less Likely (25%) — RBC/HGB/HCT measured and optimal' },
  { probability: 35, categoryId: 9255, label: 'Less Likely (35%) — partial hemoconcentration panel only' },
];

export function dehydrationCategoryFor(probability: number): ProbabilityCategory {
  if (probability >= 90) return ProbabilityCategory.HIGHLY_LIKELY;
  if (probability >= 70) return ProbabilityCategory.LIKELY;
  if (probability >= 50) return ProbabilityCategory.POSSIBLE;
  return ProbabilityCategory.LESS_LIKELY;
}

const TEXT: { [k in DehydrationType]: string } = {
  chronic: 'Chronic Dehydration is',
  acute: 'Acute Dehydration is',
  general: 'Dehydration is',
  none: 'Dehydration is',
};

const has = (m: any): boolean => !!m?.hasValue?.();
const high = (m: any): boolean => !!m?.isGreaterThanOptimal?.();

// Markers that explain the reading, in the order the panel lists them:
// hemoconcentration first, then the electrolyte, then the protein/BUN evidence
// that separates chronic from acute.
function markersFor(ba: any, type: DehydrationType): DehydrationMarker[] {
  const out: DehydrationMarker[] = [];
  const add = (m: any, label: string) => {
    if (has(m) && high(m)) out.push({ direction: 'up', label });
  };
  add(ba.rbc, 'RBC');
  add(ba.hgb, 'HGB');
  add(ba.hct, 'HCT');
  if (type === 'acute' || type === 'chronic') add(ba.sodium, 'Na');
  if (type === 'chronic') {
    add(ba.albumin, 'Albumin');
    add(ba.protein, 'Protein');
    add(ba.bun, 'BUN');
  }
  return out;
}

/**
 * Returns null when the section has nothing to say — no hemoconcentration
 * marker measured at all — so the panel can be skipped entirely the way the
 * Glucose and Adrenal panels are.
 */
export function dehydrationGauge(ba: any): DehydrationGauge | null {
  if (!ba) return null;

  const hasRbc = has(ba.rbc);
  const hasHgb = has(ba.hgb);
  const hasHct = has(ba.hct);
  if (!hasRbc && !hasHgb && !hasHct) return null;

  // Reuse the categories BloodworkAnalysis already computed, so the marker
  // rules stay in exactly one place.
  const byType: Array<[DehydrationType, ProbabilityCategory]> = [
    ['chronic', ba.chronicDehydrationProbabilityCategory],
    ['acute', ba.acuteDehydrationProbabilityCategory],
    ['general', ba.dehydrationProbabilityCategory],
  ];

  for (const [type, category] of byType) {
    if (
      !category ||
      category === ProbabilityCategory.NA ||
      category === ProbabilityCategory.LESS_LIKELY
    ) {
      continue;
    }
    const tier = TIERS[`${type}:${category}`];
    if (!tier) continue;
    return {
      probability: tier.probability,
      category: dehydrationCategoryFor(tier.probability),
      type,
      text: TEXT[type],
      driverCategoryId: tier.categoryId,
      markers: markersFor(ba, type),
    };
  }

  // Nothing fired — grade the confidence by how complete the panel is.
  const fullHemo = hasRbc && hasHgb && hasHct;
  const hasSodium = has(ba.sodium);
  const hasProteinEvidence = has(ba.albumin) || has(ba.protein) || has(ba.bun);

  let rung;
  if (fullHemo && hasSodium && hasProteinEvidence) rung = LESS_LIKELY_LADDER[0];
  else if (fullHemo && hasSodium) rung = LESS_LIKELY_LADDER[1];
  else if (fullHemo) rung = LESS_LIKELY_LADDER[2];
  else rung = LESS_LIKELY_LADDER[3];

  return {
    probability: rung.probability,
    category: dehydrationCategoryFor(rung.probability),
    type: 'none',
    text: TEXT.none,
    driverCategoryId: rung.categoryId,
    markers: [],
  };
}
